"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  FormHelperText,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Divider,
  Text,
  Tooltip,
  HStack,
  FormErrorMessage,
  useToast,
  Select,
  IconButton,
} from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import { Plus, Trash2 } from "lucide-react";

interface CustomFunctionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (functionConfig: any) => Promise<void>;
  initialValues?: any;
}

export const CustomFunctionModal: React.FC<CustomFunctionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialValues,
}) => {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [method, setMethod] = useState<
    "GET" | "POST" | "PUT" | "PATCH" | "DELETE"
  >("POST");
  const [headers, setHeaders] = useState<Array<{ key: string; value: string }>>(
    [],
  );
  const [queryParams, setQueryParams] = useState<
    Array<{ key: string; value: string }>
  >([]);
  const [parameters, setParameters] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [timeoutMs, setTimeoutMs] = useState(120000); // 2 minutes
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();

  const LEAD_CONTACT_INFO_KEY = "lead_contact_info";
  const LEAD_CONTACT_INFO_VALUE = "{{lead_contact_info}}";

  useEffect(() => {
    if (initialValues) {
      setName(initialValues.name || "");
      setDescription(initialValues.description || "");
      setUrl(initialValues.url || "");
      setMethod(initialValues.method || "POST");
      if (initialValues.headers && typeof initialValues.headers === "object") {
        const headerPairs = Object.entries(initialValues.headers).map(
          ([key, value]) => ({
            key,
            value: value as string,
          }),
        );
        setHeaders(headerPairs);
      } else {
        setHeaders([]);
      }
      if (
        initialValues.query_params &&
        typeof initialValues.query_params === "object"
      ) {
        const queryParamPairs = Object.entries(initialValues.query_params).map(
          ([key, value]) => ({
            key,
            value: value as string,
          }),
        );
        setQueryParams(queryParamPairs);
      } else {
        setQueryParams([]);
      }
      setParameters(
        initialValues.parameters
          ? JSON.stringify(initialValues.parameters, null, 2)
          : "",
      );
      setTimeoutMs(initialValues.timeout_ms || 120000);
      setNameError(null);
      setJsonError(null);
    } else {
      // defaults for "Add new"
      setName("");
      setDescription("");
      setUrl("");
      setMethod("POST");
      setHeaders([]);
      setQueryParams([]);
      setParameters(
        JSON.stringify(
          {
            type: "object",
            properties: {},
            required: [],
          },
          null,
          2,
        ),
      );
      setTimeoutMs(120000);
      setNameError(null);
      setJsonError(null);
    }
  }, [initialValues]);

  const validateToolName = (value: string): boolean => {
    if (!value.trim()) {
      setNameError("Tool name cannot be empty");
      return false;
    }
    if (value.length > 64) {
      setNameError("Tool name must be 64 characters or less");
      return false;
    }
    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validNameRegex.test(value)) {
      setNameError(
        "Tool name must only contain letters, numbers, underscores, and dashes (no spaces)",
      );
      return false;
    }
    setNameError(null);
    return true;
  };

  const validateJsonSchema = (schema: string): boolean => {
    if (!schema.trim()) {
      setJsonError(null);
      return true;
    }
    try {
      const parsed = JSON.parse(schema);
      if (parsed.type !== "object") {
        setJsonError("Schema root type must be 'object'");
        return false;
      }
      setJsonError(null);
      return true;
    } catch {
      setJsonError("Invalid JSON format");
      return false;
    }
  };

  const handleAddHeader = () => {
    setHeaders([...headers, { key: "", value: "" }]);
  };

  const handleUpdateHeader = (
    index: number,
    field: "key" | "value",
    value: string,
  ) => {
    const updated = [...headers];
    updated[index][field] = value;
    setHeaders(updated);
  };

  const handleRemoveHeader = (index: number) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const handleAddQueryParam = () => {
    setQueryParams([...queryParams, { key: "", value: "" }]);
  };

  const addLeadContactInfoParam = () => {
    setQueryParams((prev) => {
      const exists = prev.some((p) => p.key === LEAD_CONTACT_INFO_KEY);
      if (exists) {
        toast({
          title: "Already added",
          description: `${LEAD_CONTACT_INFO_KEY} is already in query parameters.`,
          status: "info",
          duration: 2500,
          isClosable: true,
        });
        return prev;
      }

      return [
        ...prev,
        { key: LEAD_CONTACT_INFO_KEY, value: LEAD_CONTACT_INFO_VALUE },
      ];
    });
  };

  const handleUpdateQueryParam = (
    index: number,
    field: "key" | "value",
    value: string,
  ) => {
    if (queryParams[index]?.key === LEAD_CONTACT_INFO_KEY) {
      toast({
        title: "Locked parameter",
        description:
          "lead_contact_info is managed by the preset button and cannot be edited.",
        status: "info",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    const updated = [...queryParams];
    if (field === "key") {
      const incoming = value.trim();
      if (incoming === LEAD_CONTACT_INFO_KEY) {
        toast({
          title: "Reserved key",
          description:
            "Please use the 'Add lead contact info' button to add this parameter.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }
    updated[index][field] = value;
    setQueryParams(updated);
  };

  const handleRemoveQueryParam = (index: number) => {
    setQueryParams(queryParams.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!validateToolName(name)) {
      toast({
        title: "Invalid tool name",
        description: nameError || "Please fix the tool name.",
        status: "error",
        duration: 3000,
      });
      return;
    }

    if (!validateJsonSchema(parameters)) {
      toast({
        title: "Invalid JSON schema",
        description: jsonError || "Please fix the schema.",
        status: "error",
        duration: 3000,
      });
      return;
    }

    setIsSaving(true);
    try {
      const parsedParameters = parameters ? JSON.parse(parameters) : {};

      const headersObject = headers
        .filter((h) => h.key && h.value)
        .reduce(
          (acc, h) => {
            acc[h.key] = h.value;
            return acc;
          },
          {} as Record<string, string>,
        );

      const queryParamsObject = queryParams
        .filter((qp) => qp.key && qp.value)
        .reduce(
          (acc, qp) => {
            acc[qp.key] = qp.value;
            return acc;
          },
          {} as Record<string, string>,
        );

      await onSave({
        type: "custom",
        name,
        description,
        url,
        method,
        ...(Object.keys(headersObject).length > 0 && {
          headers: headersObject,
        }),
        ...(Object.keys(queryParamsObject).length > 0 && {
          query_params: queryParamsObject,
        }),
        parameters: parsedParameters,
        timeout_ms: timeoutMs,
      });

      onClose();
    } catch (error: any) {
      console.error("Error saving function:", error);
      setJsonError("Invalid JSON format. Please check your syntax.");
      toast({
        title: "Error saving function",
        description:
          error?.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(parameters);
      setParameters(JSON.stringify(parsed, null, 2));
      setJsonError(null);
    } catch {
      setJsonError("Invalid JSON format. Please check your syntax.");
      toast({
        title: "Invalid JSON",
        description: "Could not format JSON. Please check your syntax.",
        status: "error",
        duration: 3000,
      });
    }
  };

  // Example JSON schemas
  const examples = {
    1: {
      type: "object",
      properties: {
        appointment_available_ts: {
          type: "string",
          description:
            "The timestamp of the appointment that is available for booking.",
        },
      },
      required: ["appointment_available_ts"],
    },
    2: {
      type: "object",
      properties: {
        city: {
          type: "string",
          description: "The city for which the weather is to be fetched.",
        },
      },
      required: ["city"],
    },
    3: {
      type: "object",
      properties: {
        appointment_available_ts: {
          type: "string",
          description:
            "The timestamp of the appointment that is available for booking.",
        },
        doctor_name: {
          type: "string",
          description: "An optional field to specify the name of the doctor.",
        },
      },
      required: ["appointment_available_ts"],
    },
  } as const;

  const loadExample = (key: keyof typeof examples) => {
    setParameters(JSON.stringify(examples[key], null, 2));
    setJsonError(null);
  };

  const blueFocus = {
    borderColor: "blue.500",
    boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent
        bg="white"
        color="gray.800"
        borderRadius="xl"
        border="1px"
        borderColor="blue.200"
        _focusWithin={{
          borderColor: "blue.300",
          boxShadow: "0 0 0 1px var(--chakra-colors-blue-300)",
        }}
      >
        <ModalHeader color="secondary.800" display="flex" alignItems="center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M4.33337 14.6667V10"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M4.33337 3.33398V1.33398"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M11.6666 14.666V12.666"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M11.6666 6.00065V1.33398"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6.33337 4.66732V8.66732C6.33337 9.40065 6.00004 10.0007 5.00004 10.0007H3.66671C2.66671 10.0007 2.33337 9.40065 2.33337 8.66732V4.66732C2.33337 3.93398 2.66671 3.33398 3.66671 3.33398H5.00004C6.00004 3.33398 6.33337 3.93398 6.33337 4.66732Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13.6666 7.33333V11.3333C13.6666 12.0667 13.3333 12.6667 12.3333 12.6667H11C9.99996 12.6667 9.66663 12.0667 9.66663 11.3333V7.33333C9.66663 6.6 9.99996 6 11 6H12.3333C13.3333 6 13.6666 6.6 13.6666 7.33333Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span style={{ marginLeft: 8 }}>Custom Function</span>
        </ModalHeader>
        <ModalCloseButton
          _hover={{ bg: "blue.50", color: "blue.700" }}
          _active={{ bg: "blue.100" }}
          _focusVisible={{
            boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
          }}
        />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired isInvalid={!!nameError}>
              <FormLabel color="secondary.700">Name</FormLabel>
              <Input
                id="custom-function-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  validateToolName(e.target.value);
                }}
                onBlur={() => validateToolName(name)}
                placeholder="Enter the name of the custom function"
                variant="outline"
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: "blue.300" }}
                _placeholder={{ color: "gray.500" }}
                focusBorderColor="blue.400"
                _focus={blueFocus}
              />
              {nameError && <FormErrorMessage>{nameError}</FormErrorMessage>}
            </FormControl>

            <FormControl isRequired>
              <FormLabel color="secondary.700">Description</FormLabel>
              <Textarea
                id="custom-function-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter the description of the custom function"
                rows={3}
                variant="outline"
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: "blue.300" }}
                _placeholder={{ color: "gray.500" }}
                focusBorderColor="blue.400"
                _focus={blueFocus}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color="secondary.700">API Endpoint</FormLabel>
              <Text fontSize="xs" color="gray.600" mb={2}>
                The API Endpoint is the address of the service you are
                connecting to
              </Text>
              <HStack spacing={2} align="flex-start">
                <Select
                  id="custom-function-method"
                  value={method}
                  onChange={(e) => setMethod(e.target.value as any)}
                  width="120px"
                  variant="outline"
                  bg="white"
                  borderColor="gray.300"
                  _hover={{ borderColor: "blue.300" }}
                  focusBorderColor="blue.400"
                  _focus={blueFocus}
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                  <option value="DELETE">DELETE</option>
                </Select>
                <Input
                  id="custom-function-url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="Enter the URL of the custom function"
                  variant="outline"
                  bg="white"
                  borderColor="gray.300"
                  _hover={{ borderColor: "blue.300" }}
                  _placeholder={{ color: "gray.500" }}
                  focusBorderColor="blue.400"
                  _focus={blueFocus}
                  flex={1}
                />
              </HStack>
            </FormControl>

            <Divider />

            <FormControl>
              <FormLabel color="secondary.700">Headers</FormLabel>
              <Text fontSize="xs" color="gray.600" mb={2}>
                Specify the HTTP headers required for your API request.
              </Text>

              {headers.map((header, index) => (
                <HStack key={index} spacing={2} mb={2}>
                  <Input
                    id={`custom-function-header-key-${index}`}
                    placeholder="Key"
                    value={header.key}
                    onChange={(e) =>
                      handleUpdateHeader(index, "key", e.target.value)
                    }
                    variant="outline"
                    bg="white"
                    borderColor="gray.300"
                    _hover={{ borderColor: "blue.300" }}
                    _placeholder={{ color: "gray.500" }}
                    focusBorderColor="blue.400"
                    _focus={blueFocus}
                    flex={1}
                  />
                  <Input
                    id={`custom-function-header-value-${index}`}
                    placeholder="Value"
                    value={header.value}
                    onChange={(e) =>
                      handleUpdateHeader(index, "value", e.target.value)
                    }
                    variant="outline"
                    bg="white"
                    borderColor="gray.300"
                    _hover={{ borderColor: "blue.300" }}
                    _placeholder={{ color: "gray.500" }}
                    focusBorderColor="blue.400"
                    _focus={blueFocus}
                    flex={1}
                  />
                  <IconButton
                    aria-label="Remove header"
                    icon={<Trash2 size={16} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveHeader(index)}
                    color="blue.500"
                    _hover={{ bg: "blue.50", color: "blue.700" }}
                    _active={{ bg: "blue.100" }}
                  />
                </HStack>
              ))}

              <Button
                leftIcon={<Plus size={16} />}
                variant="ghost"
                size="sm"
                onClick={handleAddHeader}
                borderColor="blue.300"
                color="blue.500"
                _hover={{ bg: "blue.50" }}
              >
                New key value pair
              </Button>
            </FormControl>

            <Divider />

            <FormControl>
              <HStack justify="space-between" align="center">
                <FormLabel color="secondary.700" mb={0}>
                  Query Parameters
                </FormLabel>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={addLeadContactInfoParam}
                  borderColor="blue.300"
                  color="blue.600"
                  _hover={{ bg: "blue.50" }}
                >
                  Add lead contact info
                </Button>
              </HStack>

              <Text fontSize="xs" color="gray.600" mb={2} mt={2}>
                Query string parameters to append to the URL.
              </Text>

              {queryParams.map((param, index) => {
                const isLeadContactInfo = param.key === LEAD_CONTACT_INFO_KEY;

                return (
                  <HStack key={index} spacing={2} mb={2}>
                    <Input
                      id={`custom-function-query-key-${index}`}
                      placeholder="Key"
                      value={param.key}
                      onChange={(e) =>
                        handleUpdateQueryParam(index, "key", e.target.value)
                      }
                      isReadOnly={isLeadContactInfo}
                      variant="outline"
                      bg={isLeadContactInfo ? "gray.50" : "white"}
                      borderColor="gray.300"
                      _hover={{ borderColor: "blue.300" }}
                      _placeholder={{ color: "gray.500" }}
                      focusBorderColor="blue.400"
                      _focus={blueFocus}
                      flex={1}
                      cursor={isLeadContactInfo ? "not-allowed" : "text"}
                    />

                    {isLeadContactInfo ? (
                      <Tooltip
                        label="This will be determined at runtime"
                        hasArrow
                      >
                        <Input
                          id={`custom-function-query-value-${index}`}
                          placeholder="Value"
                          value={param.value}
                          onChange={(e) =>
                            handleUpdateQueryParam(
                              index,
                              "value",
                              e.target.value,
                            )
                          }
                          isReadOnly
                          variant="outline"
                          bg="gray.50"
                          borderColor="gray.300"
                          _hover={{ borderColor: "blue.300" }}
                          _placeholder={{ color: "gray.500" }}
                          focusBorderColor="blue.400"
                          _focus={blueFocus}
                          flex={1}
                          cursor="not-allowed"
                        />
                      </Tooltip>
                    ) : (
                      <Input
                        id={`custom-function-query-value-${index}`}
                        placeholder="Value"
                        value={param.value}
                        onChange={(e) =>
                          handleUpdateQueryParam(index, "value", e.target.value)
                        }
                        variant="outline"
                        bg="white"
                        borderColor="gray.300"
                        _hover={{ borderColor: "blue.300" }}
                        _placeholder={{ color: "gray.500" }}
                        focusBorderColor="blue.400"
                        _focus={blueFocus}
                        flex={1}
                      />
                    )}

                    <IconButton
                      aria-label="Remove query parameter"
                      icon={<Trash2 size={16} />}
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveQueryParam(index)}
                      color="blue.500"
                      _hover={{ bg: "blue.50", color: "blue.700" }}
                      _active={{ bg: "blue.100" }}
                    />
                  </HStack>
                );
              })}

              <Button
                leftIcon={<Plus size={16} />}
                variant="ghost"
                size="sm"
                onClick={handleAddQueryParam}
                borderColor="blue.300"
                color="blue.500"
                _hover={{ bg: "blue.50" }}
              >
                New key value pair
              </Button>
            </FormControl>

            <Divider />

            <FormControl isInvalid={!!jsonError}>
              <FormLabel color="secondary.700">
                Parameters (Optional)
                <Text fontSize="xs" mt={1} color="gray.600">
                  JSON schema that defines the format in which the input to the
                  function is required.
                </Text>
              </FormLabel>

              <Textarea
                id="custom-function-parameters"
                value={parameters}
                onChange={(e) => setParameters(e.target.value)}
                onBlur={() => validateJsonSchema(parameters)}
                placeholder="Enter JSON Schema here..."
                minHeight="200px"
                fontFamily="mono"
                variant="outline"
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: "blue.300" }}
                _placeholder={{ color: "gray.500" }}
                focusBorderColor="blue.400"
                _focus={blueFocus}
              />
              {jsonError && <FormErrorMessage>{jsonError}</FormErrorMessage>}

              <HStack mt={3} spacing={2}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => loadExample(1)}
                  borderColor="blue.300"
                  color="blue.500"
                  _hover={{ bg: "blue.50" }}
                >
                  example 1
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => loadExample(2)}
                  borderColor="blue.300"
                  color="blue.500"
                  _hover={{ bg: "blue.50" }}
                >
                  example 2
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => loadExample(3)}
                  borderColor="blue.300"
                  color="blue.500"
                  _hover={{ bg: "blue.50" }}
                >
                  example 3
                </Button>
              </HStack>

              <Button
                mt={3}
                width="100%"
                variant="outline"
                onClick={formatJson}
                borderColor="blue.300"
                color="blue.500"
                _hover={{ bg: "blue.50" }}
              >
                Format JSON
              </Button>
            </FormControl>

            <Divider />
            <FormControl>
              <FormLabel color="secondary.700">
                Timeout (ms)
                <Tooltip label="1,000 ms (1s) minimum, 300,000 ms (5 min) maximum">
                  <InfoIcon ml={1} boxSize={3} />
                </Tooltip>
              </FormLabel>
              <NumberInput
                value={timeoutMs}
                onChange={(valueString) => setTimeoutMs(Number(valueString))}
                min={1000}
                max={300000}
                step={1000}
                focusBorderColor="blue.500"
              >
                <NumberInputField
                  bg="white"
                  borderColor="gray.300"
                  _hover={{ borderColor: "blue.300" }}
                  _focus={blueFocus}
                />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText>Maximum time to wait for response</FormHelperText>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter gap={3}>
          <Button
            variant="ghost"
            onClick={onClose}
            isDisabled={isSaving}
            color="black"
            _hover={{ bg: "gray.100" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            colorScheme="blue"
            bg="blue.500"
            color="white"
            _hover={{ bg: "blue.600" }}
            _active={{ bg: "blue.700" }}
            _focusVisible={{
              boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
            }}
            isLoading={isSaving}
            loadingText="Saving..."
            isDisabled={!!nameError || !!jsonError}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
